import React, {useEffect, useState} from "react";
import {Button, Card, Col, DatePicker, Input, Row, Spin, Table} from "antd";
import {
    CreditCardOutlined,
    DollarCircleOutlined,
    DownloadOutlined,
    InfoCircleOutlined,
    ShoppingOutlined,
    SolutionOutlined
} from "@ant-design/icons";
import moment from "moment";
import {useStore} from "../../../hooks/useStore";
import ExcelJS from "exceljs";
import {saveAs} from "file-saver";
import "./PaymentSummaryPage.css";
import {stripEmpty} from "../../../utils/stripEmpty";
import {FilterButton} from "./filterButton/FilterButton";
import {Link} from "react-router-dom";
import {codPaymentStatuses, paymentTypes} from "../../../constants/constants";
import "../../cashOnDelivery/pastPeriods/CodPastPeriodsPage.css";
import {CodAggrementBlock} from "./filterButton/codAggrementBlock/CodAggrementBlock";
import {useTranslation} from "react-i18next";
import {CustomerCodAggrementBlock} from "components/layouts/customerLayout/components/customerCodAggrementBlock/CustomerCodAggrementBlock";
import {TableWrapper} from "../../_defaultComponents/TableWrapper/TableWrapper";

const {RangePicker} = DatePicker;

const getDefaultTimeRange = () => {
    const now = moment();
    const currentHour = now.hour();

    // Case 1: Between 00:00-06:00
    if (currentHour >= 0 && currentHour < 6) {
        const startDate = moment()
            .subtract(1, "day")
            .set({hour: 6, minute: 0, second: 0, millisecond: 0});
        const endDate = moment().set({hour: 6, minute: 0, second: 0, millisecond: 0});
        return [startDate, endDate];
    }

    // Case 2: Between 06:00-23:59
    if (currentHour >= 6 && currentHour <= 23) {
        const startDate = moment().set({hour: 6, minute: 0, second: 0, millisecond: 0});
        const endDate = moment();
        return [startDate, endDate];
    }

    // Case 3: Default case (should not normally occur)
    const startDate = moment().set({hour: 6, minute: 0, second: 0, millisecond: 0});
    const endDate = moment()
        .add(1, "day")
        .set({hour: 6, minute: 0, second: 0, millisecond: 0});
    return [startDate, endDate];
};

const getStatusLabel = status => {
    const found = codPaymentStatuses.find(s => s.value === status);
    if (found) return found.name;
    switch (status) {
        case "NOT_SUCCESS":
        case "FAIL":
        case "FAILED":
            return "Ödeme Başarısız";
        case "SUCCESS":
            return "Ödendi";
        case "PENDING":
            return "Bekliyor";
        case "CANCEL":
        case "CANCELLED":
            return "İptal";
        default:
            return status || "-";
    }
};

const renderCurrency = val => {
    return val != null
        ? `${Number(val).toLocaleString("tr-TR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
          })} TL`
        : `${Number("0").toFixed(2)} TL`;
};

export const BasePaymentSummaryPage = ({filtersComponent: FiltersComponent, isBackoffice}) => {
    const {t} = useTranslation();
    const stores = useStore();
    const CREDIT = "creditCard";
    const CASH = "cash";

    // Update filters state to use date range with default time range
    const [filters, setFilters] = useState({
        startDate: getDefaultTimeRange()[0],
        endDate: getDefaultTimeRange()[1]
    });
    const [selectedPaymentType, setSelectedPaymentType] = useState("all");
    const [loading, setLoading] = useState(false);
    const [summary, setSummary] = useState({});
    const [orders, setOrders] = useState([]);
    const [detailRows, setDetailRows] = useState([]);

    const getTransactionStatusLabel = status => {
        return (
            status &&
            `${t(`codPaymentTransactionProviderPaymentStatuses.${status}`, {
                ns: "codPayment"
            })}`
        );
    };

    // Update useEffect to handle date range
    useEffect(() => {
        const {startDate, endDate} = filters;
        if (!startDate || !endDate) return;

        (async () => {
            setLoading(true);
            const raw = {
                ...filters,
                startDate: startDate.format("YYYY-MM-DD"),
                endDate: endDate.format("YYYY-MM-DD"),
                startTime: startDate.format("HH:mm"),
                endTime: endDate.format("HH:mm")
            };

            const payload = stripEmpty(raw);

            try {
                const summaryRes = await stores.codPaymentStore.getPaymentSummary(payload);
                const listRes = await stores.codPaymentStore.getPaymentSummaryList({
                    ...payload,
                    firstPaymentType: selectedPaymentType,
                    receiverName: filters.receiverName,
                    isBackoffice
                });

                setSummary(summaryRes || {});
                setDetailRows(listRes.rows || []);

                const merged = Object.values(listRes.rows).map(order => {
                    const transactions = order.transactions || [];
                    const successTypes = Array.from(
                        new Set(
                            transactions
                                .filter(tx => tx.providerPaymentStatus?.status === "SUCCESS")
                                .map(tx => tx.paymentType)
                        )
                    );
                    return {
                        ...order,
                        processedPayment: successTypes.length ? successTypes.join("/") : "-",
                        payableAmount: order.payableAmount,
                        // Filter transactions to only include SUCCESS ones when not in backoffice
                        transactions: isBackoffice
                            ? transactions
                            : transactions.filter(
                                  tx => tx.providerPaymentStatus?.status === "SUCCESS"
                              )
                    };
                });

                // Filter out orders that have no SUCCESS transactions when not in backoffice
                const filteredOrders = isBackoffice
                    ? merged
                    : merged.filter(order => order.transactions.length > 0);

                setOrders(filteredOrders);
            } catch (err) {
                console.error(err);
            } finally {
                setLoading(false);
            }
        })();
    }, [filters, selectedPaymentType, stores.codPaymentStore]);

    // Toggle card/cash selection
    const togglePaymentType = type => {
        setSelectedPaymentType(prev => (prev === type ? "all" : type));
    };

    const handleExport = async () => {
        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet("Ödemeler");

        const baseColumns = [
            {header: "Sipariş Numarası", key: "orderNumber", width: 20},
            {
                header: "Alıcı Adı",
                key: "receiverName",
                width: 25
            },
            {
                header: "Alt Firma Adı",
                key: "subCompanyName",
                width: 25,
                hidden: !isBackoffice
            },
            {
                header: "Alt Firma ID",
                key: "subCompanyId",
                width: 25,
                hidden: !isBackoffice
            },
            {header: "Kurye", key: "courierName", width: 20},
            {
                header: "Kurye ID",
                key: "courierId",
                width: 25,
                hidden: !isBackoffice
            },
            {
                header: "TCKN",
                key: "tckn",
                width: 15,
                hidden: !isBackoffice
            },
            {header: "Sipariş Tarihi", key: "orderDateOnly", width: 15},
            {
                header: "Sipariş Saati",
                key: "orderTimeOnly",
                width: 15
            },
            {
                header: "İlk Ödeme Tipi",
                key: "firstPaymentType",
                width: 15
            },
            {
                header: "Gerçekleşen Ödeme Tipi",
                key: "processedPayment",
                width: 20
            },
            {
                header: "Komisyon Oranı",
                key: "commissionRate",
                width: 15,
                render: val => {
                    return val != null ? `%${val}` : "-";
                }
            },
            {
                header: "İşlem Tutarı",
                key: "totalTransactionAmount",
                width: 20,
                style: {
                    numFmt: "0.00"
                },
                type: "number"
            },
            {
                header: "Hesaba Yatırılacak Tutar",
                key: "transactionPayableAmount",
                width: 20,
                type: "number",
                style: {
                    numFmt: "0.00"
                }
            },
            {
                header: "Durum",
                key: "status",
                width: 15,
                hidden: !isBackoffice
            },
            {
                header: "Txn Id",
                key: "transactionId",
                width: 15,
                hidden: !isBackoffice
            },
            {
                header: "Kapıda Ödeme Tarihi",
                key: "processedPaymentDate",
                width: 18
            },
            {
                header: "Kapıda Ödeme Saati",
                key: "processedPaymentTime",
                width: 18
            },
            {
                header: "Banka Transfer Tarihi",
                key: "reconciliationDate",
                width: 18,
                hidden: !isBackoffice
            }
        ];
        sheet.columns = baseColumns?.filter(c => !c?.hidden);

        // Filter detailRows for non-backoffice users to only include SUCCESS transactions
        const filteredDetailRows = isBackoffice
            ? detailRows
            : detailRows
                  .map(order => ({
                      ...order,
                      transactions:
                          order.transactions?.filter(
                              tx => tx.providerPaymentStatus?.status === "SUCCESS"
                          ) || []
                  }))
                  .filter(order => order.transactions.length > 0);

        (filteredDetailRows || []).forEach(order => {
            (order?.transactions || []).forEach(tx => {
                const baseRow = {
                    _id: order._id,
                    orderNumber: order.orderNumber,
                    receiverName: order.receiverName,
                    subCompanyName: order.subCompanyName,
                    subCompanyId: order.subCompanyId,
                    courierId: order.courierId,
                    tckn: order.tckn,
                    orderDateOnly: moment(order?.orderDate).format("DD.MM.YYYY"),
                    orderTimeOnly: moment(order?.orderDate).format("HH:mm"),
                    courierName: order.courierName,
                    firstPaymentType: getPaymentTypeLabel(order.firstPaymentType),
                    processedPayment: getPaymentTypeLabel(tx.paymentType),
                    commissionRate: tx.paymentType === "cash" ? "-" : tx.invoice.commissionRate,
                    totalTransactionAmount: Number(tx.transactionAmount || 0),
                    transactionPayableAmount: Number(tx.invoice?.payableAmount || 0),
                    status: getTransactionStatusLabel(tx.providerPaymentStatus?.status),
                    transactionId: tx.transactionId,
                    processedPaymentDate: moment(tx.createdAt).format("DD.MM.YYYY"),
                    processedPaymentTime: moment(tx.createdAt).format("HH:mm:ss"),
                    reconciliationDate:
                        tx.paymentType?.toLowerCase() === "cash"
                            ? "Nakit"
                            : moment(tx.reconciliationDate).format("DD.MM.YYYY")
                };

                const backofficeRow = isBackoffice
                    ? {
                          reconciliationDate:
                              order.status === "BANK_VERIFIED" &&
                              tx.invoice?.reconciliationDate &&
                              tx.paymentType &&
                              tx.paymentType.toLowerCase() !== "cash"
                                  ? moment(tx.invoice.reconciliationDate).format("DD.MM.YYYY")
                                  : undefined
                      }
                    : {};

                sheet.addRow({...baseRow, ...backofficeRow});
            });
        });

        sheet.getRow(1).eachCell(cell => {
            cell.font = {bold: true};
            cell.alignment = {vertical: "middle", horizontal: "center"};
        });

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buffer], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            }),
            "payment-summary.xlsx"
        );
    };

    const getPaymentTypeLabel = value => {
        const found = paymentTypes.find(pt => pt.value === value);
        return found ? found.label : value;
    };

    const columns = [
        {
            title: "Sipariş Numarası",
            dataIndex: "orderNumber",
            key: "orderNumber",
            render: (text, row) =>
                row.deliveryId ? (
                    <Link
                        target="_blank"
                        to={
                            isBackoffice
                                ? `/orders/${row.deliveryId}`
                                : `/musteri/siparislerim/${row.deliveryId}`
                        }
                    >
                        {text}
                    </Link>
                ) : (
                    text
                )
        },
        {
            title: "Firma",
            dataIndex: "companyName",
            key: "companyName",
            hideForCustomerTheme: true,
            render: (text, record) => (
                <Link to={`/companies/${record?.companyId}`} target="_blank">
                    {text}
                </Link>
            )
        },
        {
            title: "Alt Firma",
            dataIndex: "subCompanyName",
            key: "subCompanyName",
            hideForCustomerTheme: true,
            render: (text, record) => (
                <Link to={`/companies/sub-company/${record?.subCompanyId}`} target="_blank">
                    {text}
                </Link>
            )
        },
        {
            title: "Alıcı Adı",
            dataIndex: "receiverName",
            key: "receiverName"
        },
        {
            title: "Sipariş Tarihi",
            dataIndex: "orderDateOnly",
            key: "orderDateOnly",
            width: 120,
            render: val =>
                val ? moment(val, ["DD.MM.YYYY", moment.ISO_8601]).format("DD.MM.YYYY") : "-"
        },
        {
            title: "Sipariş Saati",
            dataIndex: "orderTimeOnly",
            key: "orderTimeOnly"
        },
        ...(isBackoffice
            ? [
                  {
                      title: "Kurye",
                      dataIndex: "courierName",
                      key: "courierName",
                      render: (text, row) => {
                          if (row.courierId) {
                              return (
                                  <a
                                      href={`/couriers/${row.courierId}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                  >
                                      {text}
                                  </a>
                              );
                          }
                          return text;
                      }
                  }
              ]
            : []),
        {
            title: "İlk Ödeme Tipi",
            dataIndex: "firstPaymentType",
            key: "firstPaymentType",
            render: val => getPaymentTypeLabel(val)
        },
        {
            title: "Sipariş Tutarı",
            dataIndex: "totalOrderAmount",
            key: "totalOrderAmount",
            width: 150,
            render: renderCurrency
        },
        {
            title: "Hesaba Yatırılacak Tutar",
            dataIndex: "payableAmount",
            key: "payableAmount",
            width: 160,
            render: renderCurrency
        },
        {
            title: "Gerçekleşen Ödeme Tipi",
            dataIndex: "processedPayment",
            key: "processedPayment",
            render: val => {
                if (!val) return "-";
                return val
                    .split("/")
                    .map(type => getPaymentTypeLabel(type))
                    .join(" / ");
            }
        },
        ...(isBackoffice
            ? [
                  {
                      title: "Durum",
                      dataIndex: "status",
                      key: "status",
                      render: val => getStatusLabel(val)
                  }
              ]
            : [])
    ];

    return (
        <Spin spinning={loading}>
            {/* Firma / AltFirma filtreleri */}
            <Row>
                <Col style={{marginBottom: 16, flex: 2}}>
                    <FiltersComponent
                        filters={filters}
                        setFilters={setFilters}
                        datePickerProps={{
                            showTime: true,
                            format: "DD.MM.YYYY HH:mm",
                            value: [filters.startDate, filters.endDate],
                            onChange: dates => {
                                if (dates && dates[0] && dates[1]) {
                                    setFilters(prev => ({
                                        ...prev,
                                        startDate: dates[0],
                                        endDate: dates[1]
                                    }));
                                }
                            }
                        }}
                    />
                </Col>
                <Col
                    style={{
                        marginBottom: 16,
                        display: "flex",
                        justifyContent: "flex-end",
                        flex: 1
                    }}
                >
                    {/* <CustomerCodAggrementBlock
                        codCommissionRate={summary?.codCommissionRate}
                        isBackoffice={false}
                    /> */}
                </Col>
            </Row>
            {/* Özet Kartları ve Komisyon Kutusu */}
            <Row
                gutter={[16, 16]}
                style={{
                    marginBottom: "20px",
                    alignItems: "stretch"
                }}
            >
                <Col>
                    <FilterButton
                        label="Sipariş Sayısı"
                        amount={summary.orderCount ?? "-"}
                        icon={
                            <div
                                style={{
                                    backgroundColor: "#AEFF01",
                                    borderRadius: "50%",
                                    padding: "8px"
                                }}
                            >
                                <ShoppingOutlined style={{fontSize: 36}} />
                            </div>
                        }
                    />
                </Col>
                <Col>
                    <FilterButton
                        label="Toplam Kapıda Tahsilat"
                        //topInfo={"Tahsil Edilen Tutar"}
                        //bottomInfo={"Gelen Tutar"}
                        amount={
                            summary.totalCreditCardReceivedAmount && summary.totalCashReceivedAmount
                                ? renderCurrency(
                                      summary.totalCreditCardReceivedAmount +
                                          summary.totalCashReceivedAmount
                                  )
                                : "-"
                        }
                        /*bottomAmount={
                            summary.totalCreditCardPriceAmount && summary.totalCashPriceAmount
                                ? `${(
                                      summary.totalCreditCardPriceAmount +
                                      summary.totalCashPriceAmount
                                  ).toFixed(2)} TL`
                                : "-"
                        }*/
                        icon={
                            <div
                                style={{
                                    backgroundColor: "#AEFF01",
                                    borderRadius: "50%",
                                    padding: "16px"
                                }}
                            >
                                <SolutionOutlined style={{fontSize: 28}} />
                            </div>
                        }
                        handleClick={() => togglePaymentType("all")}
                        isActive={selectedPaymentType === "all"}
                    />
                </Col>
                <Col>
                    <FilterButton
                        label="Kredi Kartı Tahsilatı"
                        topInfo={"Kredi Kartı Tipinde Gerçekleşen Tutar"}
                        bottomInfo={"Kredi Kartı Tipinde Gelen Tutar"}
                        bottomAmount={
                            summary.totalCreditCardReceivedAmount
                                ? renderCurrency(summary.totalCreditCardReceivedAmount)
                                : "-"
                        }
                        topAmount={
                            summary.totalCreditCardPriceAmount
                                ? renderCurrency(summary.totalCreditCardPriceAmount)
                                : "-"
                        }
                        icon={
                            <div
                                style={{
                                    backgroundColor: "#AEFF01",
                                    borderRadius: "50%",
                                    padding: "8px"
                                }}
                            >
                                <CreditCardOutlined style={{fontSize: 36}} />
                            </div>
                        }
                        handleClick={() => togglePaymentType(CREDIT)}
                        isActive={selectedPaymentType === CREDIT}
                    />
                </Col>
                <Col>
                    <FilterButton
                        label="Kapıda Nakit Tahsilatı"
                        topInfo={"Nakit Tipinde Gerçekleşen Tutar"}
                        bottomInfo={"Nakit Tipinde Gelen Tutar"}
                        bottomAmount={
                            summary.totalCashReceivedAmount
                                ? renderCurrency(summary.totalCashReceivedAmount)
                                : "-"
                        }
                        topAmount={
                            summary.totalCashPriceAmount
                                ? renderCurrency(summary.totalCashPriceAmount)
                                : "-"
                        }
                        icon={
                            <div
                                style={{
                                    backgroundColor: "#AEFF01",
                                    borderRadius: "50%",
                                    padding: "8px"
                                }}
                            >
                                <DollarCircleOutlined style={{fontSize: 36}} />
                            </div>
                        }
                        handleClick={() => togglePaymentType(CASH)}
                        isActive={selectedPaymentType === CASH}
                    />
                </Col>
                {/* {
                    <Col flex="auto" style={{textAlign: "right", minWidth: 260}}>
                        <CodAggrementBlock
                            codCommissionRate={summary?.codCommissionRate}
                            isBackoffice={isBackoffice}
                        />
                    </Col>
                } */}
            </Row>

            {/* Tablo */}
            <div style={{marginTop: "20px"}}>
                <TableWrapper
                    dataSource={orders}
                    columns={columns}
                    rowKey="_id"
                    pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ["10", "20", "50", "100"],
                        showTotal: (total, range) => `${range[0]}-${range[1]} / ${total} kayıt`,
                        total: orders.length
                    }}
                    expandable={{
                        expandedRowRender: rec => {
                            const dynamicDetailColumns = [
                                {
                                    title: "Txn Id",
                                    dataIndex: "transactionId",
                                    key: "transactionId",
                                    hidden: !isBackoffice
                                },
                                {
                                    title: "Oluşturulma Tarihi",
                                    dataIndex: "createdAt",
                                    key: "createdAtDate",
                                    render: dt => (dt ? moment(dt).format("DD.MM.YYYY") : "-")
                                },
                                {
                                    title: "Oluşturulma Saati",
                                    dataIndex: "createdAt",
                                    key: "createdAtTime",
                                    render: dt => (dt ? moment(dt).format("HH:mm:ss") : "-")
                                },
                                {
                                    title: "Ödeme Tipi",
                                    dataIndex: "paymentType",
                                    key: "paymentType",
                                    render: val => getPaymentTypeLabel(val)
                                },
                                {
                                    title: "Komisyon Oranı",
                                    dataIndex: ["invoice", "commissionRate"],
                                    key: "commissionRate",
                                    render: (val, record) => {
                                        if (record.paymentType?.toLowerCase() === "cash") {
                                            return "-";
                                        }
                                        return val != null ? `%${val}` : "-";
                                    }
                                },
                                {
                                    title: "Ödeme Tutarı",
                                    dataIndex: "transactionAmount",
                                    key: "transactionAmount",
                                    render: renderCurrency
                                },
                                {
                                    title: "Hesaba Yatırılacak Tutar",
                                    dataIndex: ["invoice", "payableAmount"],
                                    key: "processedPaymentAmount",
                                    render: renderCurrency
                                },
                                {
                                    title: "Durum",
                                    dataIndex: ["providerPaymentStatus", "status"],
                                    key: "status",
                                    render: val => getTransactionStatusLabel(val),
                                    hidden: !isBackoffice
                                },
                                ...(isBackoffice
                                    ? [
                                          {
                                              title: "Banka Transfer Tarihi",
                                              dataIndex: ["invoice", "reconciliationDate"],
                                              key: "reconciliationDate",
                                              render: (val, record, index, parent) => {
                                                  const order = orders.find(o =>
                                                      o.transactions?.some(
                                                          t =>
                                                              t.transactionId ===
                                                              record.transactionId
                                                      )
                                                  );
                                                  if (
                                                      order?.status === "BANK_VERIFIED" &&
                                                      val &&
                                                      record.paymentType &&
                                                      record.paymentType.toLowerCase() !== "cash"
                                                  ) {
                                                      return moment(val).format("DD.MM.YYYY");
                                                  }
                                                  return "-";
                                              }
                                          }
                                      ]
                                    : [])
                            ];
                            return (
                                <Table
                                    className="cod-transactions-table"
                                    dataSource={rec.transactions}
                                    columns={dynamicDetailColumns?.filter(c => !c?.hidden) || []}
                                    rowKey={tx => tx.transactionId}
                                    pagination={false}
                                    size="small"
                                    style={{
                                        backgroundColor: "white",
                                        borderRadius: "8px"
                                    }}
                                />
                            );
                        }
                    }}
                    bordered
                />
            </div>
        </Spin>
    );
};
